"use client";

import { useLocation } from "wouter";

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Plus,
  Search,
  Building2,
  Edit,
  Trash2,
  RefreshCw,
  Loader2,
  AlertCircle,
  CheckCircle2,
  Eye,
} from "lucide-react";
import { useState, useMemo } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import { trpc } from "@/lib/trpc";
import { useAuth } from "@/_core/hooks/useAuth";
import SalviaLayout from "@/components/SalviaLayout";
import { maskCNPJ, maskCEP, maskPhone, maskCNAE } from "@/lib/masks";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { z } from "zod";
import { useClientsTabsPreview } from "@/hooks/useClientsTabsPreview";
import ClientsTabsPreviewDrawer from "@/components/ClientsTabsPreviewDrawer";
import { ComboboxSelect } from "@/components/ui/combobox-select";
import { usePageTitle } from "@/hooks/usePageTitle";

const ESTADOS_BRASIL = [
  "AC",
  "AL",
  "AP",
  "AM",
  "BA",
  "CE",
  "DF",
  "ES",
  "GO",
  "MA",
  "MT",
  "MS",
  "MG",
  "PA",
  "PB",
  "PR",
  "PE",
  "PI",
  "RJ",
  "RN",
  "RS",
  "RO",
  "RR",
  "SC",
  "SP",
  "SE",
  "TO",
];

const GRAUS_RISCO = [
  { value: "1", label: "Grau 1 - Risco Mínimo" },
  { value: "2", label: "Grau 2 - Risco Baixo" },
  { value: "3", label: "Grau 3 - Risco Médio" },
  { value: "4", label: "Grau 4 - Risco Alto" },
];

/**
 * V24: Helper para extrair mensagem amigável de erros tRPC/Zod
 * Evita exibir JSON bruto no toast ou no formError
 */
function parseZodErrorFromTRPC(error: any): { fieldErrors: Record<string, string>; formError: string } | null {
  // tRPC v11 + Zod v4: os issues ficam em error.data.zodError.fieldErrors e .formErrors
  const zodError = error?.data?.zodError;
  if (zodError) {
    const fieldErrors: Record<string, string> = {};
    // fieldErrors é um objeto { fieldName: string[] }
    if (zodError.fieldErrors && typeof zodError.fieldErrors === "object") {
      Object.entries(zodError.fieldErrors).forEach(([field, msgs]) => {
        fieldErrors[field] = Array.isArray(msgs) ? msgs[0] : String(msgs);
      });
    }
    // formErrors é um array de strings
    const formError =
      (Array.isArray(zodError.formErrors) && zodError.formErrors.length > 0
        ? zodError.formErrors[0]
        : null) ||
      (Object.keys(fieldErrors).length > 0
        ? "Revise os campos destacados para continuar."
        : null);
    if (Object.keys(fieldErrors).length > 0 || formError) {
      return { fieldErrors, formError: formError || "Revise os campos destacados para continuar." };
    }
  }
  // Fallback: tentar parsear message como JSON (legado)
  const msg = error?.message || "";
  if (msg.startsWith("[") || msg.startsWith("{")) {
    try {
      const parsed = JSON.parse(msg);
      if (Array.isArray(parsed)) {
        const fieldErrors: Record<string, string> = {};
        parsed.forEach((issue: any) => {
          const field = issue.path?.[0] || "form";
          if (!fieldErrors[field]) fieldErrors[field] = issue.message || "Erro de validação";
        });
        return { fieldErrors, formError: "Revise os campos destacados para continuar." };
      }
    } catch {
      // não é JSON válido, ignorar
    }
  }
  return null;
}

// Schema Zod para validação frontend (mesmo do backend)
const clientFormSchema = z.object({
  channelId: z.number().int().positive("Canal é obrigatório"),
  nomeFantasia: z
    .string()
    .min(2, "Nome Fantasia deve ter pelo menos 2 caracteres")
    .trim(),
  razaoSocial: z
    .string()
    .min(2, "Razão Social deve ter pelo menos 2 caracteres")
    .trim(),
  cnpj: z.string().min(14, "CNPJ deve conter 14 dígitos"),

  // Endereço (obrigatório)
  logradouro: z
    .string()
    .min(2, "Logradouro deve ter pelo menos 2 caracteres")
    .trim(),
  numero: z.string().min(1, "Número é obrigatório").trim(),
  complemento: z.string().trim().optional().nullable(),
  bairro: z.string().min(2, "Bairro deve ter pelo menos 2 caracteres").trim(),
  cidade: z.string().min(2, "Cidade deve ter pelo menos 2 caracteres").trim(),
  estado: z
    .string()
    .regex(/^[A-Z]{2}$/, "Estado deve ser 2 letras maiúsculas (ex: SP)"),
  cep: z.string().regex(/^\d{8}$/, "CEP deve conter 8 dígitos"),

  // Dados NR1 (opcionais)
  cnae: z
    .union([
      z.literal(""),
      z.string().regex(/^\d{7}$/, "CNAE deve conter 7 dígitos"),
    ])
    .optional(),
  grauRisco: z.string().optional().or(z.literal("")),
  atividadePrincipal: z.string().optional().or(z.literal("")),
  sector: z.string().optional().or(z.literal("")),
  employeesCount: z.number().int().nonnegative().optional().or(z.literal(0)),

  // Contato (opcionais)
  email: z.string().email("E-mail inválido").optional().or(z.literal("")),
  phone: z.string().optional().or(z.literal("")),
  responsibleName: z.string().trim().optional().or(z.literal("")),

  // V11: Representante e Tipo de Venda
  isRepresentative: z.boolean().optional().default(false),
  salesType: z.enum(["DIRECT", "REPRESENTATIVE"]).optional().default("DIRECT"),
  representativeCompanyId: z.number().int().positive().optional().nullable(),
});

type ClientFormData = z.infer<typeof clientFormSchema>;

interface FieldErrors {
  [key: string]: string;
}

interface BackendErrorResponse {
  ok: false;
  error: {
    fieldErrors?: FieldErrors;
    formError?: string;
    code?: string;
  };
}

export default function Clients() {
  usePageTitle("Clientes");
  const [searchTerm, setSearchTerm] = useState("");
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const [editingClientId, setEditingClientId] = useState<number | null>(null);
  const [selectedChannelId, setSelectedChannelId] = useState<string>("");
  // V12: Filtros de Tipo de Venda e Representante
  const [salesTypeFilter, setSalesTypeFilter] = useState<"" | "DIRECT" | "REPRESENTATIVE">("" );
  const [representativeFilter, setRepresentativeFilter] = useState<string>("");
  const [statusFilter, setStatusFilter] = useState<
    "active" | "inactive" | "all"
  >("active");
  const [fieldErrors, setFieldErrors] = useState<FieldErrors>({});
  const [formError, setFormError] = useState<string>("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { user } = useAuth();
  const [, navigate] = useLocation();
  const isChannelStructuralUser = ["channel_admin", "channel_admin_multi", "channel_operator", "channel_consultant"].includes(user?.role || "");
  // Minha carteira > Clientes opera em modo visualização para todo papel estrutural Canal e também para RH cliente
  const isReadOnly = isChannelStructuralUser || user?.role === "client_rh";
  // Edição e ciclo de vida permanecem restritos ao núcleo administrativo da plataforma
  const canManageClients = user?.role === "admin" || user?.role === "salvia_operation_admin";
  // Flag para preview de abas
  const enableClientsTabsPreview = useClientsTabsPreview();
  const [previewDrawerOpen, setPreviewDrawerOpen] = useState(false);
  const [previewClient, setPreviewClient] = useState<any>(null);

  const [formData, setFormData] = useState<Partial<ClientFormData>>({
    channelId: 0,
    cnpj: "",
    razaoSocial: "",
    nomeFantasia: "",
    cnae: "",
    atividadePrincipal: "",
    grauRisco: "",
    sector: "",
    employeesCount: 0,
    logradouro: "",
    numero: "",
    complemento: "",
    bairro: "",
    cidade: "",
    estado: "",
    cep: "",
    email: "",
    phone: "",
    responsibleName: "",
  });

  const isChannelAdmin = user?.role === "channel_admin" || user?.role === "channel_admin_multi";
  const isAdminSalvia = ["admin", "salvia_operation_admin"].includes(user?.role || "");
  const utils = trpc.useUtils();

  // Canais para o filtro da listagem (todos os canais ativos)
  const { data: channels } = trpc.channels.list.useQuery(undefined, {
    enabled: isAdminSalvia,
  });

  // Canais para o dropdown do formulário (sem canais de teste vitest)
  const { data: channelsForDropdown } = trpc.channels.listForDropdown.useQuery(undefined, {
    enabled: isAdminSalvia,
  });

  // V12: Buscar representantes para o filtro condicional
  const { data: representativesData } = trpc.clients.listRepresentatives.useQuery(
    selectedChannelId && selectedChannelId !== "0"
      ? { channelId: parseInt(selectedChannelId) }
      : {},
    { enabled: salesTypeFilter === "REPRESENTATIVE" }
  );

  const {
    data: listResponse,
    isLoading,
    refetch,
  } = trpc.clients.list.useQuery(
    {
      ...(selectedChannelId && selectedChannelId !== "0" ? { channelId: parseInt(selectedChannelId) } : {}),
      ...(salesTypeFilter ? { salesType: salesTypeFilter as "DIRECT" | "REPRESENTATIVE" } : {}),
      ...(salesTypeFilter === "REPRESENTATIVE" && representativeFilter ? { representativeCompanyId: parseInt(representativeFilter) } : {}),
      // V89: Passar status ao backend para filtrar corretamente inativos
      status: statusFilter,
    } as any,
    { staleTime: 0 } // recarregar ao mudar filtro
  );

  // Extrair items da resposta estruturada
  const allClients = (listResponse as any)?.items || [];

  // Filtrar e buscar clientes
  const clients = useMemo(() => {
    let filtered = allClients || [];

    // Filtrar para mostrar: matrizes, empresas comuns e grupos econômicos
    // NÃO mostrar: filiais (empresas com matrizId preenchido)
    filtered = filtered.filter((client: any) => {
      // Esconder filiais (empresas vinculadas a uma matriz)
      if (client.matrizId) return false;
      // Mostrar tudo que não é filial: matrizes, empresas comuns, grupos econômicos
      return true;
    });

    // Status já filtrado no backend — não filtrar novamente client-side

    // Buscar por nome, razão social ou CNPJ
    if (searchTerm.trim()) {
      const term = searchTerm.toLowerCase();
      filtered = filtered.filter(
        (client: any) =>
          client.nomeFantasia?.toLowerCase().includes(term) ||
          client.razaoSocial?.toLowerCase().includes(term) ||
          client.cnpj?.includes(term)
      );
    }

    return filtered;
  }, [allClients, statusFilter, searchTerm]);

  /**
   * Consumir error contract estruturado do backend
   */
  const createClient = trpc.clients.create.useMutation({
    onSuccess: data => {
      if (data?.ok) {
        toast.success("✅ Cliente criado com sucesso!");
        setIsDialogOpen(false);
        resetForm();
        refetch();
        // V15: Invalidar cache de representantes para refletir novo representante imediatamente
        utils.clients.listRepresentatives.invalidate();
      }
    },
    onError: (error: any) => {
      // V24: Usar helper parseZodErrorFromTRPC para eliminar exibição de JSON bruto
      const parsed = parseZodErrorFromTRPC(error);
      if (parsed) {
        setFieldErrors(parsed.fieldErrors);
        setFormError(parsed.formError);
        toast.error("❌ Verifique os campos destacados");
      } else {
        // Erro genérico: garantir mensagem amigável (nunca JSON)
        const raw = error?.message || "";
        const isJson = raw.startsWith("[") || raw.startsWith("{");
        const message = isJson
          ? "Não foi possível salvar o cliente. Verifique os campos e tente novamente."
          : raw || "Não foi possível salvar o cliente. Tente novamente.";
        setFormError(message);
        toast.error(`❌ ${message}`);
      }
    },
  });

  const updateClient = trpc.clients.update.useMutation({
    onSuccess: data => {
      if (data?.ok) {
        toast.success("✅ Cliente atualizado com sucesso!");
        setIsDialogOpen(false);
        setIsEditMode(false);
        setEditingClientId(null);
        resetForm();
        refetch();
        // V15: Invalidar cache de representantes para refletir alteração de isRepresentative imediatamente
        utils.clients.listRepresentatives.invalidate();
      }
    },
    onError: (error: any) => {
      // V24: Usar helper parseZodErrorFromTRPC para eliminar exibição de JSON bruto
      const parsed = parseZodErrorFromTRPC(error);
      if (parsed) {
        setFieldErrors(parsed.fieldErrors);
        setFormError(parsed.formError);
        toast.error("❌ Verifique os campos destacados");
      } else {
        const raw = error?.message || "";
        const isJson = raw.startsWith("[") || raw.startsWith("{");
        const message = isJson
          ? "Não foi possível atualizar o cliente. Verifique os campos e tente novamente."
          : raw || "Não foi possível atualizar o cliente. Tente novamente.";
        setFormError(message);
        toast.error(`❌ ${message}`);
      }
    },
  });

  const deleteClient = trpc.clients.delete.useMutation({
    onSuccess: () => {
      toast.success("✅ Cliente desativado com sucesso.");
      refetch();
    },
    onError: (error: any) => {
      const message =
        error?.message ||
        "Não foi possível desativar o cliente. Tente novamente.";
      toast.error(`❌ ${message}`);
    },
  });

  const updateStatus = trpc.clients.updateStatus.useMutation({
    onSuccess: () => {
      toast.success("✅ Status atualizado com sucesso.");
      refetch();
    },
    onError: (error: any) => {
      const message =
        error?.message ||
        "Não foi possível atualizar o status. Tente novamente.";
      toast.error(`❌ ${message}`);
    },
  });

  const resetForm = () => {
    setFormData({
      channelId:
        isChannelAdmin && (user as any)?.channelId
          ? (user as any).channelId
          : 0,
      cnpj: "",
      razaoSocial: "",
      nomeFantasia: "",
      cnae: "",
      atividadePrincipal: "",
      grauRisco: "",
      sector: "",
      employeesCount: 0,
      logradouro: "",
      numero: "",
      complemento: "",
      bairro: "",
      cidade: "",
      estado: "",
      cep: "",
      email: "",
      phone: "",
      responsibleName: "",
      // V11
      isRepresentative: false,
      salesType: "DIRECT",
      representativeCompanyId: null,
    });
    setFieldErrors({});
    setFormError("");
    setIsEditMode(false);
    setEditingClientId(null);
  };
  // Abrir preview de abas para cliente selecionado
  const handleOpenPreview = (client: any) => {
    setPreviewClient(client);
    setPreviewDrawerOpen(true);
  };

  const handleOpenDialog = () => {
    navigate("/clients/new");
    resetForm();
    setIsDialogOpen(true); // Abrir em modo edição
  };

  const handleEditClient = (client: any) => {
    navigate(`/clients/${client.id}`);
    setFormData({
      channelId: client.channelId || 0,
      cnpj: maskCNPJ(client.cnpj || ""),
      razaoSocial: client.razaoSocial || "",
      nomeFantasia: client.nomeFantasia || "",
      cnae: client.cnae || "",
      atividadePrincipal: client.atividadePrincipal || "",
      grauRisco: client.grauRisco || client.riskGrade || "",
      sector: client.sector || "",
      employeesCount: client.employeesCount || 0,
      logradouro: client.logradouro || "",
      numero: client.numero || "",
      complemento: client.complemento || "",
      bairro: client.bairro || "",
      cidade: client.cidade || "",
      estado: client.estado || "",
      cep: maskCEP(client.cep || ""),
      email: client.email || "",
      phone: maskPhone(client.phone || ""),
      responsibleName: client.responsibleName || "",
      // V11
      isRepresentative: client.isRepresentative || false,
      salesType: client.salesType || "DIRECT",
      representativeCompanyId: client.representativeCompanyId || null,
    });
    setEditingClientId(client.id);
    setIsEditMode(true);
    setFieldErrors({});
    setFormError("");
    setIsDialogOpen(true); // Abrir em modo edição
  };

  const handleInputChange = (field: string, value: string | number) => {
    // Aplicar máscaras conforme o campo
    let maskedValue: string | number = value;

    if (field === "cnpj" && typeof value === "string") {
      maskedValue = maskCNPJ(value);
    } else if (field === "cep" && typeof value === "string") {
      maskedValue = maskCEP(value);
    } else if (field === "phone" && typeof value === "string") {
      maskedValue = maskPhone(value);
    } else if (field === "cnae" && typeof value === "string") {
      maskedValue = maskCNAE(value);
    } else if (field === "estado" && typeof value === "string") {
      maskedValue = value.toUpperCase().slice(0, 2);
    }

    setFormData(prev => ({ ...prev, [field]: maskedValue }));

    // Limpar erro do campo ao editar
    if (fieldErrors[field]) {
      setFieldErrors(prev => {
        const updated = { ...prev };
        delete updated[field];
        return updated;
      });
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setFieldErrors({});
    setFormError("");

    try {
      // Limpar máscaras antes de validar
      const cleanData = {
        ...formData,
        cnpj: (formData.cnpj as string)?.replace(/\D/g, ""),
        cep: (formData.cep as string)?.replace(/\D/g, ""),
        phone: (formData.phone as string)?.replace(/\D/g, ""),
        cnae: (formData.cnae as string)?.replace(/\D/g, ""),
      };

      // Validar com Zod (frontend)
      const validated = clientFormSchema.parse(cleanData);

      // Enviar para API
      if (isEditMode && editingClientId) {
        await updateClient.mutateAsync({
          id: editingClientId,
          ...(validated as any),
        });
      } else {
        await createClient.mutateAsync(validated as any);
      }
    } catch (error) {
      if (error instanceof z.ZodError) {
        // Erros de validação Zod do frontend
        const errors: FieldErrors = {};
        if (error.issues && Array.isArray(error.issues)) {
          error.issues.forEach((err: any) => {
            const field = err.path[0] as string;
            errors[field] = err.message;
          });
        }
        setFieldErrors(errors);
        setFormError("Revise os campos destacados para continuar.");
        toast.error("❌ Verifique os campos destacados");
      } else {
        // V24: Tentar extrair erros Zod do tRPC antes de exibir mensagem genérica
        const parsed = parseZodErrorFromTRPC(error);
        if (parsed) {
          setFieldErrors(parsed.fieldErrors);
          setFormError(parsed.formError);
          toast.error("❌ Verifique os campos destacados");
        } else {
          const raw = (error instanceof Error ? error.message : "") || "";
          const isJson = raw.startsWith("[") || raw.startsWith("{");
          const message = isJson
            ? "Não foi possível salvar o cliente. Verifique os campos e tente novamente."
            : raw || "Erro desconhecido ao salvar cliente.";
          setFormError(message);
          toast.error(`❌ ${message}`);
        }
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDeleteClient = (clientId: number, clientName: string) => {
    if (confirm(`Desativar cliente "${clientName}"?`)) {
      deleteClient.mutate({ id: clientId });
    }
  };

  const handleReactivateClient = (clientId: number, clientName: string) => {
    if (confirm(`Reativar cliente "${clientName}"?`)) {
      updateStatus.mutate({ id: clientId, status: "active" });
    }
  };

  return (
    <SalviaLayout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold text-gray-900">Clientes</h1>
            <p className="text-gray-600 mt-1">
              {["admin", "salvia_operation_admin"].includes(user?.role || "")
                ? "Gerencie os clientes da plataforma"
                : "Visualize apenas clientes ativos e aptos para operar, já aprovados e parametrizados para o seu canal"}
            </p>
          </div>
          <div className="flex gap-2 items-center">
            {enableClientsTabsPreview && (
              <Button
                variant="default"
                size="sm"
                onClick={() => {
                  if (!previewClient && clients && clients.length > 0) {
                    setPreviewClient(clients[0]);
                  }
                  if (previewClient || (clients && clients.length > 0)) {
                    setPreviewDrawerOpen(true);
                  }
                }}
                className="gap-2 bg-amber-500 hover:bg-amber-600 text-white font-semibold border-2 border-amber-600"
              >
                <Eye className="w-4 h-4" />
                Preview Abas (DEV)
              </Button>
            )}
            {canManageClients && ["admin", "salvia_operation_admin"].includes(user?.role || "") && (
              <Button onClick={handleOpenDialog} className="gap-2">
                <Plus className="w-4 h-4" />
                Novo Cliente
              </Button>
            )}
          </div>
        </div>

        {/* Filtros */}
        <div className="bg-white rounded-lg border border-gray-200 p-4 space-y-4">
          {/* Linha 1: Busca + Status + Canal */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {/* Busca */}
            <div className="relative">
              <Search className="absolute left-3 top-3 w-4 h-4 text-gray-400" />
              <Input
                placeholder="Buscar por nome, razão social ou CNPJ..."
                value={searchTerm}
                onChange={e => setSearchTerm(e.target.value)}
                className="pl-10"
              />
            </div>

            {/* Filtro de Status */}
            <Select
              value={statusFilter}
              onValueChange={(value: any) => setStatusFilter(value)}
            >
              <SelectTrigger>
                <SelectValue placeholder="Filtrar por status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="active">Ativos</SelectItem>
                {["admin", "salvia_operation_admin"].includes(user?.role || "") && <SelectItem value="inactive">Inativos</SelectItem>}
                {["admin", "salvia_operation_admin"].includes(user?.role || "") && <SelectItem value="all">Todos</SelectItem>}
              </SelectContent>
            </Select>

            {/* Filtro de Canal (apenas Admin Salvia) */}
            {isAdminSalvia && (
              <ComboboxSelect
                options={[
                  { value: "0", label: "Todos os canais" },
                  ...(channels?.map(channel => ({
                    value: channel.id.toString(),
                    label: channel.name,
                  })) || []),
                ]}
                value={selectedChannelId || "0"}
                onValueChange={setSelectedChannelId}
                placeholder="Filtrar por canal"
                searchPlaceholder="Busque pelo nome do canal..."
                emptyText="Nenhum canal encontrado."
              />
            )}
          </div>

          {/* Linha 2: V12 — Tipo de Venda + Representante condicional */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {/* Filtro Tipo de Venda */}
            <Select
              value={salesTypeFilter || "all"}
              onValueChange={(value: any) => {
                const newVal = value === "all" ? "" : value;
                setSalesTypeFilter(newVal);
                // Limpar representante ao mudar tipo
                if (newVal !== "REPRESENTATIVE") {
                  setRepresentativeFilter("");
                }
              }}
            >
              <SelectTrigger>
                <SelectValue placeholder="Tipo de Venda" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todos os tipos</SelectItem>
                <SelectItem value="DIRECT">Venda Direta</SelectItem>
                <SelectItem value="REPRESENTATIVE">Via Representante</SelectItem>
              </SelectContent>
            </Select>

            {/* Filtro Representante (condicional) */}
            {salesTypeFilter === "REPRESENTATIVE" && (
              <Select
                value={representativeFilter || "all"}
                onValueChange={(value: any) => {
                  setRepresentativeFilter(value === "all" ? "" : value);
                }}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Selecionar representante..." />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Todos os representantes</SelectItem>
                  {representativesData && representativesData.length > 0 ? (
                    representativesData.map((rep: any) => (
                      <SelectItem key={rep.id} value={String(rep.id)}>
                        {rep.nomeFantasia || rep.razaoSocial}
                      </SelectItem>
                    ))
                  ) : (
                    <SelectItem value="none" disabled>
                      Nenhum representante cadastrado
                    </SelectItem>
                  )}
                </SelectContent>
              </Select>
            )}
          </div>

          {/* Botão de Atualizar */}
          <div className="flex justify-end">
            <Button
              variant="default"
              size="sm"
              onClick={() => refetch()}
              disabled={isLoading}
              className="gap-2"
            >
              <RefreshCw
                className={`w-4 h-4 ${isLoading ? "animate-spin" : ""}`}
              />
              Atualizar
            </Button>
          </div>
        </div>

        {/* Lista de Clientes */}
        <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
          {isLoading ? (
            <div className="flex items-center justify-center p-12">
              <Loader2 className="w-6 h-6 animate-spin text-gray-400" />
            </div>
          ) : clients.length === 0 ? (
            <div className="flex flex-col items-center justify-center p-12 text-center">
              <Building2 className="w-12 h-12 text-gray-300 mb-4" />
              <p className="text-gray-600">Nenhum cliente encontrado</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50 border-b border-gray-200">
                  <tr>
                    <th className="px-6 py-3 text-left text-sm font-semibold text-gray-900">
                      Nome Fantasia
                    </th>
                    <th className="px-6 py-3 text-left text-sm font-semibold text-gray-900">
                      CNPJ
                    </th>
                    <th className="px-6 py-3 text-left text-sm font-semibold text-gray-900">
                      Razão Social
                    </th>
                    <th className="px-6 py-3 text-left text-sm font-semibold text-gray-900">
                      Tipo de Venda
                    </th>
                    <th className="px-6 py-3 text-left text-sm font-semibold text-gray-900">
                      Status
                    </th>
                    <th className="px-6 py-3 text-right text-sm font-semibold text-gray-900">
                      Ações
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {clients.map((client: any) => (
                    <tr key={client.id} className="hover:bg-gray-50">
                      <td className="px-6 py-4 text-sm font-medium text-gray-900">
                        <div className="flex items-center gap-3">
                          {client.logoUrl ? (
                            <img
                              src={client.logoUrl}
                              alt={`Logo ${client.nomeFantasia || client.name}`}
                              className="w-8 h-8 rounded object-contain bg-gray-100 flex-shrink-0"
                              onError={(e) => { (e.target as HTMLImageElement).style.display = 'none'; }}
                            />
                          ) : (
                            <div className="w-8 h-8 rounded bg-gray-200 flex items-center justify-center flex-shrink-0">
                              <Building2 className="w-4 h-4 text-gray-400" />
                            </div>
                          )}
                          <span>{client.nomeFantasia || client.name}</span>
                        </div>
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-600">
                        {maskCNPJ(client.cnpj || "")}
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-600">
                        {client.razaoSocial}
                      </td>
                      <td className="px-6 py-4 text-sm">
                        <div className="flex flex-wrap gap-1">
                          {client.isRepresentative && (
                            <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-green-50 text-green-700 border border-green-200">
                              Representante
                            </span>
                          )}
                          {client.salesType === "REPRESENTATIVE" ? (
                            <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-purple-50 text-purple-700 border border-purple-200">
                              Via Representante
                            </span>
                          ) : (
                            <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-gray-50 text-gray-600 border border-gray-200">
                              Venda Direta
                            </span>
                          )}
                        </div>
                      </td>
                      <td className="px-6 py-4 text-sm">
                        <span
                          className={`px-3 py-1 rounded-full text-xs font-medium ${
                            client.status === "active"
                              ? "bg-green-100 text-green-800"
                              : "bg-red-100 text-red-800"
                          }`}
                        >
                          {client.status === "active" ? "Ativo" : "Inativo"}
                        </span>
                      </td>
                      <td className="px-6 py-4 text-sm text-right space-x-2">
                        {isReadOnly ? (
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => navigate(`/clients/${client.id}`)}
                            className="text-blue-600 hover:text-blue-700 hover:bg-blue-50"
                            title="Visualizar cliente"
                            aria-label={`Visualizar cliente ${client.nomeFantasia || client.name}`}
                          >
                            <Eye className="w-4 h-4" />
                          </Button>
                        ) : (
                          <>
                            {enableClientsTabsPreview && (
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => handleOpenPreview(client)}
                                className="text-blue-600 hover:text-blue-700 hover:bg-blue-50"
                                title="Preview Abas (DEV)"
                              >
                                <Eye className="w-4 h-4" />
                              </Button>
                            )}
                            {canManageClients && (
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => handleEditClient(client)}
                              >
                                <Edit className="w-4 h-4" />
                              </Button>
                            )}
                            {canManageClients && (
                              client.status === "active" ? (
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() =>
                                    handleDeleteClient(
                                      client.id,
                                      client.nomeFantasia || client.name
                                    )
                                  }
                                >
                                  <Trash2 className="w-4 h-4 text-red-600" />
                                </Button>
                              ) : (
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() =>
                                    handleReactivateClient(
                                      client.id,
                                      client.nomeFantasia || client.name
                                    )
                                  }
                                >
                                  <RefreshCw className="w-4 h-4 text-green-600" />
                                </Button>
                              )
                            )}
                          </>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      {/* Dialog de Criar/Editar Cliente */}
      <Dialog open={isDialogOpen && !isEditMode} onOpenChange={setIsDialogOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto pointer-events-auto">
          <DialogHeader>
            <DialogTitle>
              {isEditMode ? "Editar Cliente" : "Novo Cliente"}
            </DialogTitle>
          </DialogHeader>

          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Alerta de Erro Geral */}
            {formError && (
              <Alert className="border-red-200 bg-red-50">
                <AlertCircle className="h-4 w-4 text-red-600" />
                <AlertDescription className="text-red-800">
                  {formError}
                </AlertDescription>
              </Alert>
            )}

            {/* Seção: Dados Básicos */}
            <div className="space-y-4 border-b pb-4">
              <h3 className="font-semibold text-gray-900">Dados Básicos</h3>

              {/* Canal (apenas Admin Salvia) */}
              {isAdminSalvia && !isEditMode && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Canal *
                  </label>
                  <ComboboxSelect
                    options={[
                      ...(channelsForDropdown || channels || []).map(channel => ({
                        value: channel.id.toString(),
                        label: (channel as any).displayName || channel.name,
                      }))
                    ]}
                    value={formData.channelId ? formData.channelId.toString() : undefined}
                    onValueChange={value => handleInputChange("channelId", parseInt(value))}
                    placeholder="Selecione um canal"
                    searchPlaceholder="Buscar canal..."
                    emptyText="Nenhum canal encontrado."
                    className={fieldErrors.channelId ? "border-red-500" : ""}
                  />
                  {fieldErrors.channelId && (
                    <p className="text-red-600 text-sm mt-1">
                      {fieldErrors.channelId}
                    </p>
                  )}
                </div>
              )}

              {/* Nome Fantasia */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Nome Fantasia *
                </label>
                <Input
                  placeholder="Ex: Empresa XYZ"
                  value={formData.nomeFantasia || ""}
                  onChange={e =>
                    handleInputChange("nomeFantasia", e.target.value)
                  }
                  className={fieldErrors.nomeFantasia ? "border-red-500" : ""}
                />
                {fieldErrors.nomeFantasia && (
                  <p className="text-red-600 text-sm mt-1">
                    {fieldErrors.nomeFantasia}
                  </p>
                )}
              </div>

              {/* Razão Social */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Razão Social *
                </label>
                <Input
                  placeholder="Ex: Empresa XYZ Ltda"
                  value={formData.razaoSocial || ""}
                  onChange={e =>
                    handleInputChange("razaoSocial", e.target.value)
                  }
                  className={fieldErrors.razaoSocial ? "border-red-500" : ""}
                />
                {fieldErrors.razaoSocial && (
                  <p className="text-red-600 text-sm mt-1">
                    {fieldErrors.razaoSocial}
                  </p>
                )}
              </div>

              {/* CNPJ */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  CNPJ *
                </label>
                <Input
                  placeholder="XX.XXX.XXX/XXXX-XX"
                  value={formData.cnpj || ""}
                  onChange={e => handleInputChange("cnpj", e.target.value)}
                  className={fieldErrors.cnpj ? "border-red-500" : ""}
                />
                {fieldErrors.cnpj && (
                  <p className="text-red-600 text-sm mt-1">
                    {fieldErrors.cnpj}
                  </p>
                )}
              </div>
            </div>

            {/* Seção: Endereço Completo (sempre aberto) */}
            <div className="space-y-4 border-b pb-4">
              <h3 className="font-semibold text-gray-900">Endereço Completo</h3>

              {/* Logradouro */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Logradouro *
                </label>
                <Input
                  placeholder="Ex: Rua das Flores"
                  value={formData.logradouro || ""}
                  onChange={e =>
                    handleInputChange("logradouro", e.target.value)
                  }
                  className={fieldErrors.logradouro ? "border-red-500" : ""}
                />
                {fieldErrors.logradouro && (
                  <p className="text-red-600 text-sm mt-1">
                    {fieldErrors.logradouro}
                  </p>
                )}
              </div>

              {/* Número e Complemento */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Número *
                  </label>
                  <Input
                    placeholder="Ex: 123"
                    value={formData.numero || ""}
                    onChange={e => handleInputChange("numero", e.target.value)}
                    className={fieldErrors.numero ? "border-red-500" : ""}
                  />
                  {fieldErrors.numero && (
                    <p className="text-red-600 text-sm mt-1">
                      {fieldErrors.numero}
                    </p>
                  )}
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Complemento
                  </label>
                  <Input
                    placeholder="Ex: Apto 101"
                    value={formData.complemento || ""}
                    onChange={e =>
                      handleInputChange("complemento", e.target.value)
                    }
                  />
                </div>
              </div>

              {/* Bairro e Cidade */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Bairro *
                  </label>
                  <Input
                    placeholder="Ex: Centro"
                    value={formData.bairro || ""}
                    onChange={e => handleInputChange("bairro", e.target.value)}
                    className={fieldErrors.bairro ? "border-red-500" : ""}
                  />
                  {fieldErrors.bairro && (
                    <p className="text-red-600 text-sm mt-1">
                      {fieldErrors.bairro}
                    </p>
                  )}
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Cidade *
                  </label>
                  <Input
                    placeholder="Ex: São Paulo"
                    value={formData.cidade || ""}
                    onChange={e => handleInputChange("cidade", e.target.value)}
                    className={fieldErrors.cidade ? "border-red-500" : ""}
                  />
                  {fieldErrors.cidade && (
                    <p className="text-red-600 text-sm mt-1">
                      {fieldErrors.cidade}
                    </p>
                  )}
                </div>
              </div>

              {/* Estado e CEP */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Estado *
                  </label>
                  <Select
                    value={formData.estado || ""}
                    onValueChange={value => handleInputChange("estado", value)}
                  >
                    <SelectTrigger
                      className={fieldErrors.estado ? "border-red-500" : ""}
                    >
                      <SelectValue placeholder="Selecione" />
                    </SelectTrigger>
                    <SelectContent>
                      {ESTADOS_BRASIL.map(estado => (
                        <SelectItem key={estado} value={estado}>
                          {estado}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  {fieldErrors.estado && (
                    <p className="text-red-600 text-sm mt-1">
                      {fieldErrors.estado}
                    </p>
                  )}
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    CEP *
                  </label>
                  <Input
                    placeholder="XXXXX-XXX"
                    value={formData.cep || ""}
                    onChange={e => handleInputChange("cep", e.target.value)}
                    className={fieldErrors.cep ? "border-red-500" : ""}
                  />
                  {fieldErrors.cep && (
                    <p className="text-red-600 text-sm mt-1">
                      {fieldErrors.cep}
                    </p>
                  )}
                </div>
              </div>
            </div>

            {/* Seção: Dados NR1 */}
            <div className="space-y-4 border-b pb-4">
              <h3 className="font-semibold text-gray-900">Dados NR1</h3>

              {/* CNAE */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  CNAE
                </label>
                <Input
                  placeholder="XXXXXXX"
                  value={formData.cnae || ""}
                  onChange={e => handleInputChange("cnae", e.target.value)}
                  className={fieldErrors.cnae ? "border-red-500" : ""}
                />
                {fieldErrors.cnae && (
                  <p className="text-red-600 text-sm mt-1">
                    {fieldErrors.cnae}
                  </p>
                )}
              </div>

              {/* Grau de Risco */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Grau de Risco
                </label>
                <Select
                  value={formData.grauRisco || ""}
                  onValueChange={value => handleInputChange("grauRisco", value)}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Selecione" />
                  </SelectTrigger>
                  <SelectContent>
                    {GRAUS_RISCO.map(grau => (
                      <SelectItem key={grau.value} value={grau.value}>
                        {grau.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Atividade Principal */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Atividade Principal
                </label>
                <Input
                  placeholder="Ex: Comércio"
                  value={formData.atividadePrincipal || ""}
                  onChange={e =>
                    handleInputChange("atividadePrincipal", e.target.value)
                  }
                />
              </div>

              {/* Setor */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Setor
                </label>
                <Input
                  placeholder="Ex: Varejo"
                  value={formData.sector || ""}
                  onChange={e => handleInputChange("sector", e.target.value)}
                />
              </div>

              {/* Número de Funcionários */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Número de Funcionários
                </label>
                <Input
                  type="number"
                  placeholder="Ex: 50"
                  value={formData.employeesCount || ""}
                  onChange={e =>
                    handleInputChange(
                      "employeesCount",
                      parseInt(e.target.value) || 0
                    )
                  }
                />
              </div>
            </div>

            {/* Seção: Contato */}
            <div className="space-y-4">
              <h3 className="font-semibold text-gray-900">Contato</h3>

              {/* Email */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  E-mail
                </label>
                <Input
                  type="email"
                  placeholder="contato@empresa.com.br"
                  value={formData.email || ""}
                  onChange={e => handleInputChange("email", e.target.value)}
                  className={fieldErrors.email ? "border-red-500" : ""}
                />
                {fieldErrors.email && (
                  <p className="text-red-600 text-sm mt-1">
                    {fieldErrors.email}
                  </p>
                )}
              </div>

              {/* Telefone */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Telefone
                </label>
                <Input
                  placeholder="(XX) XXXXX-XXXX"
                  value={formData.phone || ""}
                  onChange={e => handleInputChange("phone", e.target.value)}
                  className={fieldErrors.phone ? "border-red-500" : ""}
                />
                {fieldErrors.phone && (
                  <p className="text-red-600 text-sm mt-1">
                    {fieldErrors.phone}
                  </p>
                )}
              </div>

              {/* Responsável */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Responsável
                </label>
                <Input
                  placeholder="Nome do responsável"
                  value={formData.responsibleName || ""}
                  onChange={e =>
                    handleInputChange("responsibleName", e.target.value)
                  }
                  className={
                    fieldErrors.responsibleName ? "border-red-500" : ""
                  }
                />
                {fieldErrors.responsibleName && (
                  <p className="text-red-600 text-sm mt-1">
                    {fieldErrors.responsibleName}
                  </p>
                )}
              </div>
            </div>

            {/* Botões de Ação */}
            <div className="flex justify-end gap-3 border-t pt-4">
              <Button
                type="button"
                variant="default"
                onClick={() => setIsDialogOpen(false)}
                disabled={isSubmitting}
              >
                Cancelar
              </Button>
              <Button
                type="submit"
                disabled={
                  isSubmitting ||
                  createClient.isPending ||
                  updateClient.isPending
                }
                className="gap-2"
              >
                {isSubmitting ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin" />
                    Salvando...
                  </>
                ) : (
                  <>
                    <CheckCircle2 className="w-4 h-4" />
                    {isEditMode ? "Atualizar" : "Criar"} Cliente
                  </>
                )}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Preview de Abas */}
      {(isReadOnly || enableClientsTabsPreview) && previewClient && (
        <ClientsTabsPreviewDrawer
          isOpen={previewDrawerOpen}
          onOpenChange={setPreviewDrawerOpen}
          client={previewClient}
          isEditMode={isEditMode && editingClientId === previewClient.id}
          isLoading={isLoading}
          onRefresh={() => refetch()}
        />
      )}
    </SalviaLayout>
  );
}
